import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { MessageCircle, X, Send, Sparkles, CheckCircle2, Truck, MapPin, HelpCircle, Briefcase, Mic, MicOff, Loader2, Calendar, Hash } from 'lucide-react';
import TripFormInBot from './TripFormInBot';
import BidFormInBot, { BidSuccessCard } from './BidFormInBot';
import WhatsAppPanel from './WhatsAppPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// True only for a REAL phone (top-level window with a mobile user-agent).
// - Inside an iframe (Emergent editor preview pane, any embed) → treat as
//   desktop so the in-page panel opens (a wa.me/deep-link can't load in-frame).
// - Desktops, touchscreen laptops, iPad-desktop-mode → desktop (in-page panel).
// - Real phones (Android/iPhone/…) → deep-link to the WhatsApp app.
const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  try { if (window.self !== window.top) return false; } catch { return false; }
  const ua = navigator.userAgent || '';
  return /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
};

// Proper WhatsApp brand glyph (lucide has no brand icon).
const WhatsAppIcon = ({ className = 'w-6 h-6' }) => (
  <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
    <path d="M16.003 3.2c-7.06 0-12.79 5.73-12.79 12.79 0 2.253.59 4.457 1.712 6.4L3.2 28.8l6.57-1.717a12.74 12.74 0 006.233 1.588h.005c7.06 0 12.79-5.73 12.79-12.79 0-3.417-1.33-6.63-3.746-9.047A12.7 12.7 0 0016.003 3.2zm0 23.36h-.004a10.57 10.57 0 01-5.386-1.475l-.386-.23-3.9 1.023 1.04-3.8-.251-.4a10.56 10.56 0 01-1.62-5.638c0-5.867 4.775-10.64 10.65-10.64 2.844 0 5.516 1.108 7.525 3.12a10.57 10.57 0 013.117 7.527c0 5.867-4.775 10.64-10.65 10.64zm5.84-7.964c-.32-.16-1.894-.935-2.188-1.042-.294-.107-.508-.16-.722.16-.213.32-.828 1.042-1.015 1.256-.187.213-.374.24-.694.08-.32-.16-1.352-.498-2.576-1.59-.952-.85-1.595-1.9-1.782-2.22-.187-.32-.02-.492.14-.652.144-.143.32-.374.48-.56.16-.187.213-.32.32-.534.107-.213.053-.4-.027-.56-.08-.16-.722-1.74-.99-2.384-.26-.625-.525-.54-.722-.55l-.615-.01c-.213 0-.56.08-.853.4-.294.32-1.12 1.095-1.12 2.67 0 1.575 1.146 3.098 1.306 3.312.16.213 2.255 3.442 5.463 4.826.763.33 1.36.526 1.824.674.766.243 1.464.209 2.016.127.615-.092 1.894-.775 2.162-1.523.267-.747.267-1.388.187-1.523-.08-.133-.294-.213-.614-.373z"/>
  </svg>
);


// Routes where the widget should NOT show — payment redirect / admin
// shouldn't have a chat bubble eating the corner.
const HIDDEN_ROUTES = ['/admin', '/payment/', '/payment-result'];

const STORAGE_KEY = 'horsego_chat_session_id';

const ChatWidget = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [unread, setUnread] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  // `mode` is null while the user is on the welcome card; switches to one of
  // `get_quote` / `track_booking` / `general` / `transporter` once they pick.
  const [mode, setMode] = useState(null);
  // Embedded auth: when set to 'customer' or 'transporter', the panel shows
  // a login form INSIDE the bot instead of redirecting to /customer/login or
  // /drivers/login. On success we drop the user straight into the relevant
  // chat mode without ever leaving the page.
  const [embeddedAuth, setEmbeddedAuth] = useState(null);
  const [authView, setAuthView] = useState('signin'); // signin | signup-prompt
  const [authForm, setAuthForm] = useState({ email: '', password: '' });
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [whatsappUrl, setWhatsappUrl] = useState('');
  // === Transporter bidding-from-bot state ===
  // 'list'  → show the picker of open requests
  // 'form'  → show the bid form for `bidSelectedRequest`
  // null    → no overlay; normal chat
  const [bidView, setBidView] = useState(null);
  const [bidRequests, setBidRequests] = useState([]);
  const [bidLoading, setBidLoading] = useState(false);
  const [bidLoadError, setBidLoadError] = useState('');
  const [bidSelectedRequest, setBidSelectedRequest] = useState(null);
  const [bidSuccess, setBidSuccess] = useState(null);
  // Voice-to-text on the chat input — universal for any mode (customer Q&A,
  // tracking, transporter chat including dictating a quote).
  //   Primary path: Web Speech API (free, instant, supports fr/en/es/nl).
  //   Fallback:     MediaRecorder + POST /api/chat/transcribe (OpenAI Whisper),
  //                 used on Safari/Firefox where Web Speech is missing/limited.
  const SpeechRecognition = (typeof window !== 'undefined') && (
    window.SpeechRecognition || window.webkitSpeechRecognition
  );
  const voiceInputSupported = !!SpeechRecognition;
  const mediaRecorderSupported =
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    !!navigator?.mediaDevices?.getUserMedia;
  // Show the mic button if either path is available.
  const dictationSupported = voiceInputSupported || mediaRecorderSupported;
  const [inputListening, setInputListening] = useState(false);
  const [inputVoiceError, setInputVoiceError] = useState('');
  const [transcribing, setTranscribing] = useState(false);
  const inputRecognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Pull the logged-in customer's full profile (if any) so the agent can
  // pre-fill known fields when creating a quote and look up bookings.
  // We also check for a transporter session so the For-Transporters mode
  // can short-circuit straight to the transporter dashboard.
  const { customer, transporter, isAuthenticated, isTransporter } = (() => {
    let c = null;
    let t = null;
    try {
      const rawC = sessionStorage.getItem('customer');
      if (rawC) c = JSON.parse(rawC);
    } catch {
      // ignore corrupt sessionStorage
    }
    try {
      const rawT = sessionStorage.getItem('transporter');
      if (rawT) t = JSON.parse(rawT);
    } catch {
      // ignore corrupt sessionStorage
    }
    return {
      customer: c,
      transporter: t,
      isAuthenticated: !!c?.email,
      isTransporter: !!t?.user_id,
    };
  })();

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current && mode) {
      setTimeout(() => inputRef.current?.focus(), 150);
      setUnread(false);
    }
  }, [open, mode]);

  // Fetch the WhatsApp deep-link once on mount so the dedicated WhatsApp
  // button works immediately (independent of the AI chat).
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/chat/whatsapp-url`)
      .then(({ data }) => { if (alive && data?.url) setWhatsappUrl(data.url); })
      .catch(() => { /* button falls back to a default wa.me link */ });
    return () => { alive = false; };
  }, []);

  // Hide on payment / admin pages
  const shouldHide = HIDDEN_ROUTES.some(r => location.pathname.startsWith(r));
  if (shouldHide) return null;

  // WhatsApp deep-link that triggers the bot's 4-option welcome menu.
  // "Menu" (any case) is a menu keyword on the backend → bot replies with the
  // 4 options. New users get the menu automatically too.
  const waNumber = (whatsappUrl.match(/wa\.me\/(\d+)/) || [])[1] || '212626007465';
  const waMenuHref = `https://wa.me/${waNumber}?text=${encodeURIComponent('Menu')}`;
  // Native app deep-link (opens the WhatsApp app directly on mobile).
  const waAppDeepLink = `whatsapp://send?phone=${waNumber}&text=${encodeURIComponent('Menu')}`;

  // === Mode handling ===
  const startMode = async (newMode) => {
    // Auth-gate the modes that need a session: Get-a-Quote always requires
    // a customer login; "For Transporters" requires a transporter login
    // when the user wants to manage their account. We show the login form
    // INSIDE the bot — much smoother than redirecting away mid-conversation.
    if (newMode === 'get_quote' && !isAuthenticated) {
      setMode(newMode);
      setEmbeddedAuth('customer');
      setAuthView('signin');
      setAuthError('');
      setAuthForm({ email: '', password: '' });
      setMessages([]);
      return;
    }
    if (newMode === 'transporter' && !isTransporter) {
      setMode(newMode);
      setEmbeddedAuth('transporter');
      setAuthView('signin');
      setAuthError('');
      setAuthForm({ email: '', password: '' });
      setMessages([]);
      return;
    }
    // Authenticated Get-a-Quote → show the structured trip form (with voice
    // input) instead of an AI conversation. The AI is still helpful for FAQs
    // and tracking, but a form is a much faster way to collect trip data.
    if (newMode === 'get_quote' && isAuthenticated) {
      setMode(newMode);
      setEmbeddedAuth(null);
      setMessages([]);
      setWhatsappUrl('');
      return;
    }
    setMode(newMode);
    setEmbeddedAuth(null);
    setMessages([]);
    setWhatsappUrl('');
    // Seed the conversation with a mode-specific opening message from the AI.
    const seed = {
      get_quote: isAuthenticated
        ? "I'd like to get a quote for transporting my horse(s)."
        : "I'd like to get a quote.",
      track_booking: "I want to track my booking.",
      general: "I have a question about HorseGO.",
      transporter: isTransporter
        ? "I'm a transporter — I want help managing my quotes and requests."
        : "I want to become a transporter on HorseGO.",
    }[newMode];
    await sendMessage(seed, newMode, false /* hideUserBubble */);
  };

  // === Embedded auth handler ===
  const submitEmbeddedAuth = async (e) => {
    if (e) e.preventDefault();
    if (authLoading) return;
    setAuthError('');
    setAuthLoading(true);
    try {
      if (embeddedAuth === 'customer') {
        const { data } = await axios.post(`${API}/customers/login`, {
          email: authForm.email.trim().toLowerCase(),
          password: authForm.password,
        });
        // Mirror the same sessionStorage shape that `CustomerLoginPage` writes.
        sessionStorage.setItem('customer', JSON.stringify({
          session_token: data.session_token,
          customer_id: data.customer_id,
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          phone: data.phone || '',
          city: data.city || '',
        }));
        // Resume the Get-a-Quote flow now that we have an authenticated session.
        setEmbeddedAuth(null);
        // Force a remount so isAuthenticated / customer are re-read from sessionStorage.
        window.location.reload();
      } else {
        // Transporter login — same endpoint our TransporterLoginPage uses.
        const { data } = await axios.post(`${API}/transporters/login`, {
          login: authForm.email.trim().toLowerCase(),
          password: authForm.password,
        });
        sessionStorage.setItem('transporter', JSON.stringify(data.transporter));
        setEmbeddedAuth(null);
        window.location.reload();
      }
    } catch (err) {
      setAuthError(
        err.response?.data?.detail
        || 'Login failed. Please double-check your credentials.'
      );
    } finally {
      setAuthLoading(false);
    }
  };

  const switchToSignUp = () => {
    // The full register flow needs name/phone/city + email verification, which
    // is too much for an inline form. Send them to the dedicated page.
    if (embeddedAuth === 'customer') {
      navigate('/customer/register');
    } else {
      navigate('/drivers');
    }
    setOpen(false);
  };

  const sendMessage = async (text, modeOverride, showUserBubble = true) => {
    const trimmed = (text || '').trim();
    if (!trimmed || sending) return;
    setSending(true);
    setInput('');
    if (showUserBubble) {
      setMessages(prev => [...prev, { role: 'user', text: trimmed, actions: [] }]);
    }

    try {
      const { data } = await axios.post(`${API}/chat/message`, {
        message: trimmed,
        session_id: sessionId,
        language: i18n.language?.slice(0, 2) || 'en',
        customer_email: customer?.email || null,
        mode: modeOverride || mode || 'general',
        is_authenticated: isAuthenticated,
        is_transporter: isTransporter,
        customer: customer ? {
          first_name: customer.first_name,
          last_name: customer.last_name,
          email: customer.email,
          phone: customer.phone || customer.contact_number || '',
        } : null,
        transporter: transporter ? {
          user_id: transporter.user_id,
          email: transporter.email,
          company_name: transporter.company_name,
        } : null,
      });
      if (data.session_id) {
        setSessionId(data.session_id);
        localStorage.setItem(STORAGE_KEY, data.session_id);
      }
      if (data.whatsapp_url) setWhatsappUrl(data.whatsapp_url);
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: data.reply,
        actions: data.actions || [],
      }]);
      if (!open) setUnread(true);
    } catch (e) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: "I'm having trouble right now. Please tap 'Chat on WhatsApp' to reach our team directly.",
        actions: [],
      }]);
    } finally {
      setSending(false);
    }
  };

  // Convenience wrapper used by the input form
  const send = (text) => sendMessage(text || input, mode, true);

  // === Voice dictation for the chat input ===
  const langForRecognition = () => {
    const m = { en: 'en-US', fr: 'fr-FR', es: 'es-ES', nl: 'nl-NL' };
    return m[i18n.language?.slice(0, 2)] || 'en-US';
  };

  const toggleInputVoice = () => {
    if (inputListening || transcribing) {
      // Stop whichever path is active.
      if (inputRecognitionRef.current) {
        try { inputRecognitionRef.current.stop(); } catch {
          // ignore
        }
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try { mediaRecorderRef.current.stop(); } catch {
          // ignore
        }
      }
      setInputListening(false);
      return;
    }
    setInputVoiceError('');

    // --- Primary: Web Speech API ---
    if (voiceInputSupported) {
      const recognition = new SpeechRecognition();
      recognition.lang = langForRecognition();
      recognition.interimResults = true;
      recognition.continuous = false;
      let finalTranscript = '';
      recognition.onresult = (event) => {
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const text = event.results[i][0].transcript;
          if (event.results[i].isFinal) finalTranscript += text;
          else interim += text;
        }
        setInput((finalTranscript + interim).trim());
      };
      recognition.onerror = (e) => {
        if (e?.error === 'not-allowed' || e?.error === 'service-not-allowed') {
          setInputVoiceError(
            (t('chat.voice.permissionDenied') || 'Microphone permission denied.')
            + ' ' + (t('chat.voice.howToFix') || "Click the 🔒 / 🎤 icon in your browser's address bar, set Microphone to 'Allow', then refresh this page.")
          );
        } else if (e?.error === 'no-speech') {
          setInputVoiceError(t('chat.voice.noSpeech') || "I didn't hear anything — try again.");
        } else if (e?.error === 'audio-capture') {
          setInputVoiceError(t('chat.voice.noMic') || 'No microphone detected on this device.');
        }
        setInputListening(false);
      };
      recognition.onend = () => {
        setInputListening(false);
        if (inputRef.current) setTimeout(() => inputRef.current?.focus(), 50);
      };
      try {
        recognition.start();
        inputRecognitionRef.current = recognition;
        setInputListening(true);
      } catch {
        setInputVoiceError(t('chat.voice.startError') || 'Could not start voice capture.');
      }
      return;
    }

    // --- Fallback: MediaRecorder + Whisper backend ---
    if (mediaRecorderSupported) {
      navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
        mediaStreamRef.current = stream;
        // Pick a mime type the browser can actually encode — Safari prefers
        // mp4, Chrome/FF default to webm/opus.
        const candidates = [
          'audio/webm;codecs=opus',
          'audio/webm',
          'audio/mp4',
          'audio/ogg;codecs=opus',
        ];
        let mimeType = '';
        for (const c of candidates) {
          if (window.MediaRecorder.isTypeSupported?.(c)) { mimeType = c; break; }
        }
        const rec = mimeType
          ? new window.MediaRecorder(stream, { mimeType })
          : new window.MediaRecorder(stream);
        audioChunksRef.current = [];
        rec.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
        };
        rec.onstop = async () => {
          // Tear down mic immediately so the browser indicator stops
          try { mediaStreamRef.current?.getTracks().forEach(tr => tr.stop()); } catch {
            // ignore
          }
          mediaStreamRef.current = null;
          setInputListening(false);

          const blobType = rec.mimeType || mimeType || 'audio/webm';
          const blob = new Blob(audioChunksRef.current, { type: blobType });
          audioChunksRef.current = [];
          if (blob.size === 0) {
            setInputVoiceError(t('chat.voice.noSpeech') || "I didn't hear anything — try again.");
            return;
          }

          setTranscribing(true);
          try {
            const ext = blobType.includes('mp4') ? 'm4a'
              : blobType.includes('ogg') ? 'ogg'
              : 'webm';
            const fd = new FormData();
            fd.append('audio', blob, `chat.${ext}`);
            fd.append('language', (i18n.language || 'en').slice(0, 2));
            const { data } = await axios.post(`${API}/chat/transcribe`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            const text = (data?.text || '').trim();
            if (text) {
              // Append to whatever the user had typed.
              setInput((prev) => (prev ? `${prev} ${text}` : text));
              if (inputRef.current) setTimeout(() => inputRef.current?.focus(), 50);
            } else {
              setInputVoiceError(t('chat.voice.noSpeech') || "I didn't hear anything — try again.");
            }
          } catch (err) {
            setInputVoiceError(t('chat.voice.transcribeError') || 'Could not transcribe audio. Please try again.');
          } finally {
            setTranscribing(false);
          }
        };
        mediaRecorderRef.current = rec;
        rec.start();
        setInputListening(true);
      }).catch((err) => {
        if (err?.name === 'NotAllowedError') {
          setInputVoiceError(
            (t('chat.voice.permissionDenied') || 'Microphone permission denied.')
            + ' ' + (t('chat.voice.howToFix') || "Click the 🔒 / 🎤 icon in your browser's address bar, set Microphone to 'Allow', then refresh this page.")
          );
        } else if (err?.name === 'NotFoundError') {
          setInputVoiceError(t('chat.voice.noMic') || 'No microphone detected on this device.');
        } else {
          setInputVoiceError(t('chat.voice.startError') || 'Could not start voice capture.');
        }
      });
      return;
    }

    // No path available
    setInputVoiceError(t('chat.voice.unsupported') || 'Voice input is not supported in this browser.');
  };

  // === Transporter: open the in-bot bid picker ===
  const openBidPicker = async () => {
    if (!transporter?.user_id) return;
    setBidView('list');
    setBidSelectedRequest(null);
    setBidSuccess(null);
    setBidLoadError('');
    setBidLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/chat/transporter/open-requests`,
        { params: { transporter_user_id: transporter.user_id, limit: 5 } },
      );
      setBidRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      setBidLoadError(t('chat.bid.loadError') || 'Could not load open requests. Please try again.');
      setBidRequests([]);
    } finally {
      setBidLoading(false);
    }
  };

  const handleAction = (action) => {
    if (action.type === 'open_trip_form') {
      const qp = new URLSearchParams();
      if (action.params.pickup) qp.set('pickup', action.params.pickup);
      if (action.params.dropoff) qp.set('dropoff', action.params.dropoff);
      if (action.params.date) qp.set('date', action.params.date);
      if (action.params.horses) qp.set('horses', action.params.horses);
      navigate(`/trip-form?${qp.toString()}`);
      setOpen(false);
    } else if (action.type === 'goto_bookings' || action.type === 'trip_request_created') {
      navigate('/dashboard');
      setOpen(false);
    } else if (action.type === 'open_login') {
      navigate('/customer/login');
      setOpen(false);
    } else if (action.type === 'open_register') {
      navigate('/customer/register');
      setOpen(false);
    } else if (action.type === 'open_transporter_login') {
      navigate('/drivers/login');
      setOpen(false);
    } else if (action.type === 'open_transporter_register') {
      navigate('/drivers');
      setOpen(false);
    } else if (action.type === 'open_transporter_dashboard') {
      navigate('/drivers/dashboard');
      setOpen(false);
    } else if (action.type === 'open_bid_picker') {
      openBidPicker();
    } else if (action.type === 'whatsapp_handoff' && whatsappUrl) {
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const actionLabel = (type) => {
    switch (type) {
      case 'open_trip_form': return t('chat.cta.openForm') || 'Open trip form →';
      case 'create_trip_request': return t('chat.cta.creatingRequest') || 'Creating request…';
      case 'trip_request_created': return t('chat.cta.viewRequest') || 'View your request →';
      case 'goto_bookings': return t('chat.cta.viewBookings') || 'View my bookings →';
      case 'open_login': return t('chat.cta.signIn') || 'Sign in';
      case 'open_register': return t('chat.cta.register') || 'Create an account';
      case 'open_transporter_login': return t('chat.cta.transporterSignIn') || 'Transporter sign in';
      case 'open_transporter_register': return t('chat.cta.transporterRegister') || 'Become a transporter';
      case 'open_transporter_dashboard': return t('chat.cta.transporterDashboard') || 'Go to my dashboard →';
      case 'open_bid_picker': return t('chat.cta.openBidPicker') || 'Browse open requests →';
      case 'whatsapp_handoff': return t('chat.cta.whatsapp') || 'Chat on WhatsApp';
      default: return 'Continue';
    }
  };

  const actionStyle = (type) => {
    // Primary (amber-filled) for the recommended next step; outline-emerald otherwise.
    if (
      type === 'open_register' ||
      type === 'open_transporter_register' ||
      type === 'open_transporter_dashboard' ||
      type === 'open_bid_picker' ||
      type === 'trip_request_created' ||
      type === 'open_trip_form'
    ) {
      return 'bg-amber-500 hover:bg-amber-400 text-black font-bold';
    }
    return 'bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/30';
  };

  const resetToMenu = () => {
    setMode(null);
    setEmbeddedAuth(null);
    setAuthError('');
    setAuthForm({ email: '', password: '' });
    setMessages([]);
    setInput('');
    setBidView(null);
    setBidSelectedRequest(null);
    setBidSuccess(null);
    setBidRequests([]);
    setBidLoadError('');
  };

  // === Welcome card content ===
  const modeOptions = [
    {
      key: 'get_quote',
      icon: Truck,
      label: t('chat.modes.getQuote') || 'Get a Quote',
      sub: t('chat.modes.getQuoteSub') || 'Tell me about your trip and I\'ll set up a request.',
      color: 'from-amber-400 to-amber-600',
    },
    {
      key: 'track_booking',
      icon: MapPin,
      label: t('chat.modes.track') || 'Track a Booking',
      sub: t('chat.modes.trackSub') || 'Check the status of an existing trip.',
      color: 'from-emerald-400 to-emerald-600',
    },
    {
      key: 'transporter',
      icon: Briefcase,
      label: t('chat.modes.transporter') || 'For Transporters',
      sub: isTransporter
        ? (t('chat.modes.transporterSubSignedIn') || 'Manage your quotes and active bookings.')
        : (t('chat.modes.transporterSub') || 'Join HorseGO as a verified transporter.'),
      color: 'from-purple-400 to-purple-600',
    },
    {
      key: 'general',
      icon: HelpCircle,
      label: t('chat.modes.askQuestion') || 'Answer a Question',
      sub: t('chat.modes.askQuestionSub') || 'How HorseGO works, pricing, service area, etc.',
      color: 'from-blue-400 to-blue-600',
    },
  ];

  return (
    <>
      {/* Floating launcher — "Ask HorseGO" AI button hidden per request. */}
      {false && !open && !waOpen && (
        <button
          onClick={() => { setWaOpen(false); setOpen(true); }}
          aria-label="Open chat assistant"
          data-testid="chat-launcher-btn"
          className="fixed bottom-6 right-6 z-[60] group flex items-center gap-2 bg-gradient-to-br from-amber-400 to-amber-600 hover:from-amber-300 hover:to-amber-500 text-black font-bold px-4 py-3 rounded-full shadow-2xl shadow-amber-500/30 transition-all hover:scale-105 active:scale-95"
        >
          <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-black/15">
            <MessageCircle className="w-5 h-5" />
            {unread && (
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500" />
              </span>
            )}
          </span>
          <span className="hidden sm:inline pr-1 text-sm">
            {t('chat.launcher') || 'Ask HorseGO'}
          </span>
        </button>
      )}

      {/* Dedicated WhatsApp channel.
          Mobile → deep-link straight to the WhatsApp app with a "Menu" prefill
          so the bot shows the 4-option welcome menu (native anchor, loop-free).
          Desktop/web → open the in-page WhatsApp-style panel (4 options, login
          gate, embedded trip form, voice). */}
      {!open && !waOpen && (
        <a
          href={waMenuHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            // Desktop/web → open the in-page panel.
            // Mobile → fire the native WhatsApp app deep-link (whatsapp://),
            // with the wa.me link as a fallback if the app isn't installed.
            if (!isMobileDevice()) {
              e.preventDefault();
              setOpen(false);
              setWaOpen(true);
              return;
            }
            e.preventDefault();
            let opened = false;
            const onHide = () => { opened = true; };
            document.addEventListener('visibilitychange', onHide, { once: true });
            window.location.href = waAppDeepLink;
            // If the app didn't take over, fall back to the wa.me universal link.
            setTimeout(() => {
              document.removeEventListener('visibilitychange', onHide);
              if (!opened && document.visibilityState === 'visible') {
                window.location.href = waMenuHref;
              }
            }, 1200);
          }}
          aria-label="Chat with us on WhatsApp"
          data-testid="whatsapp-launcher-btn"
          className="fixed bottom-6 right-6 z-[60] group flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white shadow-2xl shadow-[#25D366]/40 transition-all hover:scale-105 active:scale-95"
          title="WhatsApp"
        >
          <WhatsAppIcon className="w-7 h-7" />
          <span className="absolute inline-flex h-full w-full rounded-full bg-[#25D366] opacity-40 animate-ping -z-10" />
        </a>
      )}

      {/* In-page WhatsApp-style panel (desktop) */}
      <WhatsAppPanel
        open={waOpen}
        onClose={() => setWaOpen(false)}
        waDeepLink={whatsappUrl || 'https://wa.me/212626007465'}
      />

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-[400px] h-[600px] max-h-[85vh] bg-[#0d0d0d] border border-amber-500/30 rounded-2xl shadow-2xl shadow-black/60 flex flex-col overflow-hidden"
          data-testid="chat-panel"
        >
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-amber-500/20 via-amber-400/10 to-transparent border-b border-amber-500/20 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-black" />
              </div>
              <div>
                <p className="text-white font-bold text-sm leading-tight">HorseGO AI</p>
                <p className="text-emerald-400 text-xs flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                  {t('chat.online') || 'Online'} · {t('chat.repliesIn') || 'instant replies'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {mode && (
                <button
                  onClick={resetToMenu}
                  data-testid="chat-back-to-menu-btn"
                  className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded-md hover:bg-white/10 transition-colors"
                  title="Back to menu"
                >
                  ← {t('chat.menu') || 'Menu'}
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors"
                data-testid="chat-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* === Mode picker (welcome card) === */}
          {!mode && (
            <div className="flex-1 overflow-y-auto px-5 py-6 bg-gradient-to-b from-black/40 to-[#0a0a0a]" data-testid="chat-mode-picker">
              <div className="mb-6">
                <h3 className="text-white font-bold text-xl leading-tight mb-1.5">
                  {t('chat.welcome.title') || 'How can I help today?'}
                </h3>
                <p className="text-gray-400 text-sm">
                  {t('chat.welcome.subtitle') || 'Pick one to get started — I\'ll take it from there.'}
                </p>
              </div>
              <div className="space-y-3">
                {modeOptions.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.key}
                      onClick={() => startMode(opt.key)}
                      data-testid={`chat-mode-${opt.key}`}
                      className="w-full text-left group flex items-center gap-4 bg-white/5 hover:bg-white/[0.08] border border-white/10 hover:border-amber-500/40 rounded-xl p-4 transition-all"
                    >
                      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${opt.color} flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}>
                        <Icon className="w-5 h-5 text-black" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold text-sm">{opt.label}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{opt.sub}</p>
                      </div>
                      <span className="text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                    </button>
                  );
                })}
              </div>
              {(isAuthenticated || isTransporter) && (
                <p className="text-center text-emerald-400 text-xs mt-4 inline-flex items-center justify-center gap-1.5 w-full">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  {t('chat.signedInAs') || 'Signed in as'}{' '}
                  {isTransporter
                    ? `${transporter.company_name || transporter.email} (transporter)`
                    : customer.email}
                </p>
              )}
            </div>
          )}

          {/* === Embedded auth (Get Quote / Transporter while not signed in) === */}
          {mode && embeddedAuth && (
            <div
              className="flex-1 overflow-y-auto px-5 py-6 bg-gradient-to-b from-black/40 to-[#0a0a0a]"
              data-testid={`chat-embedded-auth-${embeddedAuth}`}
            >
              <div className="mb-5">
                <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-semibold mb-3 ${
                  embeddedAuth === 'transporter'
                    ? 'bg-purple-500/20 text-purple-300'
                    : 'bg-amber-500/20 text-amber-300'
                }`}>
                  {embeddedAuth === 'transporter' ? (
                    <><Briefcase className="w-3 h-3" /> {t('chat.auth.transporterTag') || 'Transporter'}</>
                  ) : (
                    <><Truck className="w-3 h-3" /> {t('chat.auth.customerTag') || 'Customer'}</>
                  )}
                </div>
                <h3 className="text-white font-bold text-lg leading-tight mb-1.5">
                  {embeddedAuth === 'transporter'
                    ? (t('chat.auth.transporterTitle') || 'Sign in as a transporter')
                    : (t('chat.auth.customerTitle') || 'Sign in to continue')}
                </h3>
                <p className="text-gray-400 text-sm">
                  {embeddedAuth === 'transporter'
                    ? (t('chat.auth.transporterSubtitle') || 'Sign in to manage quotes and bookings — or create a transporter account.')
                    : (t('chat.auth.customerSubtitle') || 'You need a free account to request quotes. It only takes 30 seconds.')}
                </p>
              </div>

              <form onSubmit={submitEmbeddedAuth} className="space-y-3" data-testid="chat-auth-form">
                <div>
                  <label className="block text-gray-300 text-xs font-medium mb-1.5">
                    {t('chat.auth.email') || 'Email'}
                  </label>
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    value={authForm.email}
                    onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                    placeholder="you@example.com"
                    disabled={authLoading}
                    data-testid="chat-auth-email-input"
                    className="w-full bg-white/5 border border-white/10 focus:border-amber-500/50 focus:outline-none rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-xs font-medium mb-1.5">
                    {t('chat.auth.password') || 'Password'}
                  </label>
                  <input
                    type="password"
                    required
                    autoComplete="current-password"
                    value={authForm.password}
                    onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                    placeholder="••••••••"
                    disabled={authLoading}
                    data-testid="chat-auth-password-input"
                    className="w-full bg-white/5 border border-white/10 focus:border-amber-500/50 focus:outline-none rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-gray-500"
                  />
                </div>

                {authError && (
                  <div
                    role="alert"
                    data-testid="chat-auth-error"
                    className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs"
                  >
                    {authError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={authLoading || !authForm.email || !authForm.password}
                  data-testid="chat-auth-submit-btn"
                  className="w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold rounded-full py-2.5 text-sm transition-colors"
                >
                  {authLoading
                    ? (t('chat.auth.signingIn') || 'Signing in…')
                    : (t('chat.auth.signIn') || 'Sign in')}
                </button>
              </form>

              <div className="mt-4 pt-4 border-t border-white/10 text-center">
                <p className="text-gray-400 text-xs mb-2">
                  {t('chat.auth.noAccount') || "Don't have an account yet?"}
                </p>
                <button
                  onClick={switchToSignUp}
                  data-testid="chat-auth-signup-btn"
                  className="text-amber-400 hover:text-amber-300 text-sm font-semibold transition-colors"
                >
                  {embeddedAuth === 'transporter'
                    ? (t('chat.auth.becomeTransporter') || 'Become a transporter →')
                    : (t('chat.auth.createAccount') || 'Create a free account →')}
                </button>
              </div>
            </div>
          )}

          {/* === In-bot trip form (authenticated Get a Quote) === */}
          {mode === 'get_quote' && isAuthenticated && !embeddedAuth && (
            <TripFormInBot
              customer={customer}
              language={i18n.language?.slice(0, 2) || 'en'}
              t={t}
              onCreated={(requestId, fromButton) => {
                if (fromButton) {
                  navigate('/dashboard');
                  setOpen(false);
                }
              }}
            />
          )}

          {/* === Conversation === */}
          {mode && !embeddedAuth && !(mode === 'get_quote' && isAuthenticated) && (
            <>
              {/* Sticky transporter toolbar — quick access to bidding */}
              {mode === 'transporter' && isTransporter && !bidView && (
                <div className="px-3 py-2 border-b border-white/10 bg-purple-500/5 flex items-center justify-between gap-2">
                  <span className="text-xs text-purple-200 truncate">
                    <Briefcase className="w-3 h-3 inline mr-1" />
                    {transporter?.company_name || transporter?.email}
                  </span>
                  <button
                    type="button"
                    onClick={openBidPicker}
                    data-testid="chat-open-bid-picker-btn"
                    className="text-xs font-semibold bg-amber-500 hover:bg-amber-400 text-black px-3 py-1.5 rounded-full transition-colors flex items-center gap-1"
                  >
                    <Briefcase className="w-3 h-3" />
                    {t('chat.bid.browseRequests') || 'Browse open requests'}
                  </button>
                </div>
              )}

              {/* Bid picker / form — replaces the conversation while active */}
              {bidView === 'list' && (
                <div
                  className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-black/30"
                  data-testid="bid-picker-list"
                >
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="text-sm font-semibold text-white">
                      {t('chat.bid.openRequestsTitle') || 'Open trip requests'}
                    </h4>
                    <button
                      type="button"
                      onClick={() => { setBidView(null); setBidSelectedRequest(null); setBidSuccess(null); }}
                      data-testid="bid-picker-close-btn"
                      className="text-xs text-gray-400 hover:text-white"
                    >
                      × {t('chat.bid.close') || 'Close'}
                    </button>
                  </div>
                  {bidLoading && (
                    <div className="flex items-center justify-center py-6 text-gray-400 text-xs gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('chat.bid.loading') || 'Loading requests…'}
                    </div>
                  )}
                  {!bidLoading && bidLoadError && (
                    <div data-testid="bid-picker-error" className="text-xs text-red-300 bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2">
                      {bidLoadError}
                    </div>
                  )}
                  {!bidLoading && !bidLoadError && bidRequests.length === 0 && (
                    <div data-testid="bid-picker-empty" className="text-xs text-gray-400 bg-white/5 border border-white/10 rounded-lg px-3 py-4 text-center">
                      {t('chat.bid.empty') || "No open requests right now — check back soon!"}
                    </div>
                  )}
                  {!bidLoading && bidRequests.map((r) => {
                    const mapUrl = `${API}/maps/static-map?origin=${encodeURIComponent(r.pickup_address)}&destination=${encodeURIComponent(r.dropoff_address)}&width=360&height=120`;
                    return (
                      <button
                        type="button"
                        key={r.request_id}
                        onClick={() => { setBidSelectedRequest(r); setBidView('form'); }}
                        data-testid={`bid-request-card-${r.request_id}`}
                        className="w-full text-left rounded-xl border border-white/10 bg-white/5 hover:bg-white/[0.08] hover:border-amber-500/40 overflow-hidden transition-colors"
                      >
                        <img
                          src={mapUrl}
                          alt=""
                          className="w-full h-24 object-cover bg-black/40"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <div className="p-3 space-y-1.5 text-xs">
                          <div className="text-amber-300 font-mono text-[10px]">{r.request_id}</div>
                          <div className="flex items-start gap-1.5 text-white">
                            <MapPin className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                            <div className="leading-snug flex-1 min-w-0">
                              <div className="truncate">{r.pickup_address}</div>
                              <div className="text-gray-400 truncate">→ {r.dropoff_address}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-gray-300 pt-0.5">
                            {r.transport_date && (
                              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {r.transport_date}</span>
                            )}
                            {r.horse_count && (
                              <span className="flex items-center gap-1"><Hash className="w-3 h-3" /> {r.horse_count}</span>
                            )}
                          </div>
                          <div className="pt-1 text-amber-300 font-semibold text-[11px]">
                            {t('chat.bid.tapToBid') || 'Tap to submit a bid →'}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {bidView === 'form' && bidSelectedRequest && (
                <BidFormInBot
                  request={bidSelectedRequest}
                  transporterUserId={transporter.user_id}
                  onBack={() => { setBidSelectedRequest(null); setBidView('list'); }}
                  onSubmitted={(quote) => { setBidSuccess(quote); setBidView('success'); }}
                />
              )}

              {bidView === 'success' && (
                <div className="flex-1 overflow-y-auto px-3 py-3 bg-black/30">
                  <BidSuccessCard
                    quote={bidSuccess}
                    onDone={() => { setBidSuccess(null); openBidPicker(); }}
                  />
                </div>
              )}

              {/* Default conversation view */}
              {!bidView && (
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gradient-to-b from-black/40 to-[#0a0a0a]"
                data-testid="chat-messages"
              >
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] ${m.role === 'user' ? 'order-2' : ''}`}>
                      <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words ${
                        m.role === 'user'
                          ? 'bg-amber-500 text-black rounded-br-sm font-medium'
                          : 'bg-white/5 text-gray-100 border border-white/10 rounded-bl-sm'
                      }`}>
                        {m.text}
                      </div>
                      {m.actions && m.actions.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {m.actions.map((a, idx) => (
                            <button
                              key={idx}
                              onClick={() => handleAction(a)}
                              data-testid={`chat-action-${a.type}`}
                              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${actionStyle(a.type)}`}
                            >
                              {actionLabel(a.type)}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="px-3 py-2 rounded-2xl bg-white/5 border border-white/10">
                      <div className="flex gap-1">
                        <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '120ms' }} />
                        <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '240ms' }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* Input */}
              {!bidView && inputVoiceError && (
                <div
                  data-testid="chat-input-voice-error"
                  className="px-3 pt-2 pb-1 text-xs text-red-300 flex items-start gap-1.5 bg-red-500/5"
                >
                  <span>{inputVoiceError}</span>
                  <button
                    type="button"
                    onClick={() => setInputVoiceError('')}
                    className="ml-auto text-red-300 hover:text-white"
                    aria-label="Dismiss"
                  >
                    ×
                  </button>
                </div>
              )}
              {!bidView && (
              <form
                onSubmit={(e) => { e.preventDefault(); send(); }}
                className="px-3 py-3 border-t border-white/10 bg-black/40 flex items-center gap-2"
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={inputListening
                    ? (t('chat.voice.listening') || 'Listening… tap to stop')
                    : transcribing
                      ? (t('chat.voice.transcribing') || 'Transcribing…')
                      : (t('chat.inputPlaceholder') || 'Type your message…')}
                  disabled={sending || transcribing}
                  data-testid="chat-input"
                  className="flex-1 bg-white/5 border border-white/10 focus:border-amber-500/50 focus:outline-none rounded-full px-4 py-2 text-white text-sm placeholder:text-gray-500"
                  maxLength={2000}
                />
                {dictationSupported && (
                  <button
                    type="button"
                    onClick={toggleInputVoice}
                    disabled={sending}
                    data-testid="chat-voice-input-btn"
                    aria-label={inputListening ? 'Stop dictation' : 'Dictate by voice'}
                    title={transcribing
                      ? (t('chat.voice.transcribing') || 'Transcribing…')
                      : inputListening
                        ? (t('chat.voice.listening') || 'Listening… tap to stop')
                        : (t('chat.voice.dictate') || 'Dictate by voice')}
                    className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ${
                      inputListening
                        ? 'bg-red-500 hover:bg-red-400 text-white animate-pulse'
                        : transcribing
                          ? 'bg-amber-500/30 text-amber-200 border border-amber-500/40 animate-pulse'
                          : 'bg-white/5 hover:bg-white/10 text-emerald-300 border border-emerald-500/30'
                    }`}
                  >
                    {inputListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={sending || !input.trim()}
                  data-testid="chat-send-btn"
                  className="w-9 h-9 rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-black flex items-center justify-center transition-colors flex-shrink-0"
                  aria-label="Send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
              )}

              {/* WhatsApp handoff */}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
